import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, LockIcon } from '@itsrever/design-system'
import { clsx } from 'clsx'
import {
  REQUIRED_FIELDS,
  Warehouse,
  WarehouseField,
  WarehouseUpdate,
  buildMetadata,
  externalIdText,
  isMetadataTooLarge,
  metadataRowProblems,
  normalizePhone,
  sameMetadata,
  toMetadataRows,
  withBlankRow,
  warehouseProblems
} from '@/domain/warehouses'
import { SaveBar } from '@/ui/shared'
import { MetadataRows } from './metadata-rows'

const { Badge, Button, Card, Input, FieldRoot, FieldLabel, FieldError } = Base

const FIELDS: { field: WarehouseField; half?: boolean }[] = [
  { field: 'company' },
  { field: 'email' },
  { field: 'firstName', half: true },
  { field: 'lastName', half: true },
  { field: 'addressLine1' },
  { field: 'addressLine2' },
  { field: 'city', half: true },
  { field: 'postcode', half: true },
  { field: 'stateProvince', half: true },
  { field: 'stateProvinceCode', half: true },
  { field: 'country', half: true },
  { field: 'countryCode', half: true },
  { field: 'phone' }
]

interface WarehouseFormProps {
  warehouse: Warehouse
  editable: boolean
  // Stores whose routing rules send returns here: settings-api refuses to delete it while any do.
  usedBy: number
  saving: boolean
  deleting: boolean
  onSave: (warehouse: WarehouseUpdate) => void
  onDelete: () => void
  onDiscardNew: () => void
}

export const WarehouseForm: React.FC<WarehouseFormProps> = ({
  warehouse,
  editable,
  usedBy,
  saving,
  deleting,
  onSave,
  onDelete,
  onDiscardNew
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(warehouse)
  const loadedExternalId = externalIdText(warehouse.metadata)
  const [externalId, setExternalId] = useState(loadedExternalId ?? '')
  const loadedRows = () => {
    const rows = toMetadataRows(warehouse.metadata)
    return editable ? withBlankRow(rows) : rows
  }
  const [metadataRows, setMetadataRows] = useState(loadedRows)
  // Problems show once a save is attempted, not while the operator is still typing.
  const [showProblems, setShowProblems] = useState(false)
  const isNew = !warehouse.id
  const metadata = buildMetadata(warehouse.metadata, externalId, metadataRows)
  const metadataChanged = !sameMetadata(metadata, warehouse.metadata)
  const changes =
    FIELDS.filter(({ field }) => draft[field] !== warehouse[field]).length +
    (metadataChanged ? 1 : 0)
  const problems = warehouseProblems(draft)
  const rowProblems = metadataRowProblems(metadataRows)
  const metadataTooLarge = isMetadataTooLarge(metadata)

  const save = () => {
    if (
      Object.keys(problems).length ||
      Object.keys(rowProblems).length ||
      metadataTooLarge
    ) {
      setShowProblems(true)
      return
    }
    onSave({
      ...draft,
      phone: normalizePhone(draft.phone),
      // settings-api keeps metadata when omitted, so it is only sent once changed.
      metadata: metadataChanged ? metadata : undefined
    })
  }

  const discard = () => {
    if (isNew) return onDiscardNew()
    setDraft(warehouse)
    setExternalId(loadedExternalId ?? '')
    setMetadataRows(loadedRows())
    setShowProblems(false)
  }

  return (
    <Card variant="flat" className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 truncate text-sm font-medium text-neutral-900">
          {draft.company || t('warehouses.list.new_warehouse')}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {editable ? null : (
            <Badge tone="info" icon={<LockIcon />}>
              {t('warehouses.form.read_only')}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(({ field, half }) => {
          const problem = showProblems ? problems[field] : undefined
          return (
            <FieldRoot
              key={field}
              invalid={!!problem}
              className={clsx(!half && 'col-span-2')}
            >
              <FieldLabel>
                {t(`warehouses.form.${field}`)}
                {REQUIRED_FIELDS.includes(field) && editable ? ' *' : null}
              </FieldLabel>
              <Input
                value={draft[field]}
                disabled={!editable}
                onChange={(event) =>
                  setDraft({ ...draft, [field]: event.target.value })
                }
              />
              {problem ? (
                <FieldError match>
                  {t(
                    problem === 'required'
                      ? 'warehouses.form.problem_required'
                      : `warehouses.form.problem_invalid_${field}`
                  )}
                </FieldError>
              ) : null}
            </FieldRoot>
          )
        })}

        <MetadataRows
          rows={metadataRows}
          problems={showProblems ? rowProblems : {}}
          tooLarge={showProblems && metadataTooLarge}
          changed={metadataChanged}
          editable={editable}
          onChange={(rows) => setMetadataRows(withBlankRow(rows))}
          externalId={externalId}
          externalIdLocked={loadedExternalId === undefined}
          onExternalIdChange={setExternalId}
        />
      </div>

      {!editable ? (
        <p className="flex items-center gap-1.5 border-t border-solid border-neutral-100 pt-3 text-xs text-neutral-500">
          <LockIcon className="size-3" />
          {t('warehouses.form.edit_from_group')}
        </p>
      ) : isNew ? null : (
        <div className="flex max-w-[260px] flex-col gap-1 border-t border-solid border-neutral-100 pt-3">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={usedBy > 0}
            loading={deleting}
            onClick={onDelete}
            className="self-start"
          >
            {t('warehouses.form.delete')}
          </Button>
          {usedBy > 0 ? (
            <p className="text-xs text-neutral-500">
              {t('warehouses.form.delete_blocked', { count: usedBy })}
            </p>
          ) : null}
        </div>
      )}
      <SaveBar
        editable={editable}
        pending={isNew}
        changes={changes}
        saveLabel={t('warehouses.form.save')}
        saving={saving}
        onDiscard={discard}
        onSave={save}
      />
    </Card>
  )
}
